import assessmentsApi from '@/app/redux/endpoints/assessments';
import { useUpdateGroupMutation } from '@/app/redux/endpoints/groups';
import { useBulkDeleteStudentsMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  setSelectedGroupIds,
  setSelectedStudentIds,
  setSelectedTab,
} from '@/app/redux/slices/insightsSlice';
import {
  closeConfirmationDialogue,
  openConfirmationDialogue,
  setAttemptedProFeature,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { Group } from '@/common/types/group';
import { Student } from '@/common/types/student';
import { FeatureSwitch } from '@/common/ui/FeatureGate';
import AddToGroupModal from '@/common/ui/modals/AddToGroupModal';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { ProIcon } from '@/common/ui/UpgradeButton';
import generateLoginCards from '@/common/utils/generateLoginCards';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import CreateAssessmentDialogue from '@/dashboard/Assessment/CreateAssessmentDialogue';
import BulkUpdateSignIns from '@/dashboard/LearnerList/BulkUpdateSignIns';
import {
  ArrowsDownUpIcon,
  CardsIcon,
  CaretUpIcon,
  ChartLineIcon,
  FileArrowUpIcon,
  GearIcon,
  KeyIcon,
  PencilLineIcon,
  TableIcon,
  UsersFourIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { chunk } from 'lodash';
import { Info, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';
import BulkLevelChangeDialog from './BulkLevelChangeDialog';
import BulkSettingsDialog from './BulkSettingsDialog';

interface SelectedLearnerOptionsProps {
  selectedStudentIds: Set<string>;
  group: Group | undefined;
  onClose: () => void;
}
export default function SelectedLearnerOptions({
  selectedStudentIds,
  group,
  onClose,
}: SelectedLearnerOptionsProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { students, studentsById } = useStudents();
  const [bulkDeleteStudents] = useBulkDeleteStudentsMutation();
  const [updateGroup] = useUpdateGroupMutation();

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [levelChangeDialogOpen, setLevelChangeDialogOpen] = useState(false);
  const [signInChangeDialogOpen, setSignInChangeDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [assessmentDialogueOpen, setAssessmentDialogueOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const createAssessmentsDisabledTooltip = useMemo(() => {
    // get the names of the first three students with assessments
    const names = Array.from(selectedStudentIds)
      .map((id) => studentsById.get(id))
      .filter((student) => student !== undefined)
      .filter((student) => student.assessmentId)
      .slice(0, 3)
      .map((s) => `${s.studentFirstName} ${s.studentSurname}`);

    // return a tooltip corresponding
    switch (names.length) {
      case 0:
        return null;
      case 1:
        return `${names[0]} already has an active assessment`;
      case 2:
        return `${names[0]} and ${names[1]} already have active assessments`;
      default:
        return `${names[0]}, ${names[1]} and others already have active assessments`;
    }
  }, [studentsById, selectedStudentIds]);

  const selectedStudents = useMemo(
    () =>
      students.filter((s) => s && selectedStudentIds.has(s.id)) as Student[],
    [students, selectedStudentIds],
  );

  const csvData = useMemo(
    () =>
      selectedStudents.map((student) => ({
        Name: `${student.studentFirstName} ${student.studentSurname}`,
        Username: student.spellcastUsername,
        Password: student.password,
      })),
    [selectedStudents],
  );
  const csvFields = ['Name', 'Username', 'Password'];
  const csvFileName = 'selected-students.csv';

  function handleConfirmRemoveFromGroup() {
    if (!group) return;
    setConfirmationDialogueCallback(removeLeanersFromGroup);
    dispatch(
      openConfirmationDialogue({
        title: `Remove ${selectedStudentIds.size} ${
          selectedStudentIds.size === 1 ? 'learner' : 'learners'
        } from ${group.name}`,
        description: `Are you sure you want to remove ${
          selectedStudentIds.size === 1 ? 'this learner' : 'these learners'
        } from the group?`,
        variant: 'destructive',
      }),
    );
  }
  async function removeLeanersFromGroup() {
    if (!group?.id || !currentOrganisationId) {
      console.error('Group ID not found');
      toast.error('Failed to remove learners from group', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    try {
      const updatedIds = group.studentIds.filter(
        (id) => !selectedStudentIds.has(id),
      );
      await updateGroup({
        orgId: currentOrganisationId,
        groupId: group.id,
        updates: { studentIds: updatedIds },
      }).unwrap();

      toast.success(`Learners removed from ${group.name}`);

      onClose();
    } catch (error) {
      console.error('Error removing learners from group:', error);
      toast.error('Failed to remove learners from group', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function handleConfirmDeleteSelectedStudents() {
    setConfirmationDialogueCallback(handleDeleteSelectedStudents);
    dispatch(
      openConfirmationDialogue({
        title: `Delete ${selectedStudentIds.size} ${
          selectedStudentIds.size === 1 ? 'learner' : 'learners'
        }`,
        description: `Are you sure you want to delete ${
          selectedStudentIds.size === 1 ? 'this learner' : 'these learners'
        }? This action cannot be undone.`,
        variant: 'destructive',
      }),
    );
  }

  async function handleDeleteSelectedStudents() {
    if (!currentOrganisationId) {
      console.error('Missing org ID');
      toast.error('Failed to delete learners', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const allIds = Array.from(selectedStudentIds);

    // Close confirmation dialogue immediately before starting batched operation
    dispatch(closeConfirmationDialogue());

    const chunks = chunk(allIds, 50);
    let deleted = 0;

    const toastId = toast.loading('Deleting learners...', {
      description: "Please don't refresh or leave the page",
    });

    try {
      for (const idChunk of chunks) {
        const { deletedAssessmentIds } = await bulkDeleteStudents({
          orgId: currentOrganisationId,
          studentIds: idChunk,
        }).unwrap();
        if (deletedAssessmentIds.length > 0) {
          dispatch(
            assessmentsApi.util.updateQueryData(
              'getAssessmentsForOrganisation',
              currentOrganisationId,
              (draft) => {
                const ids = new Set(deletedAssessmentIds);
                return draft.filter((a) => !ids.has(a.id));
              },
            ),
          );
        }
        deleted += idChunk.length;
        toast.loading(`Deleting learners (${deleted}/${allIds.length})`, {
          id: toastId,
        });
      }

      trackMixpanelEvent('Learners Deleted', {
        studentIds: allIds,
      });

      toast.success('Learners deleted', { id: toastId, description: '' });
      onClose();
    } catch (error) {
      console.error('Error deleting learners:', error);
      if (deleted > 0) {
        toast.error('Failed to delete learners', {
          id: toastId,
          description: `Deleted ${deleted} of ${allIds.length}. Please retry.`,
        });
      } else {
        toast.error('Failed to delete learners', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    }
  }

  function handleViewInsights() {
    dispatch(setSelectedStudentIds(Array.from(selectedStudentIds)));
    dispatch(setSelectedGroupIds([]));
    dispatch(setSelectedTab('learners'));

    trackMixpanelEvent('View Insights from Learner List', {
      studentIds: Array.from(selectedStudentIds),
    });

    navigate('/dashboard/insights');
  }

  return (
    <StudentOptionsContainer>
      <p className="whitespace-nowrap font-medium mx-4">
        {selectedStudentIds.size} selected
      </p>
      <StudentOptionsButtonGroup>
        {/* Direct action buttons */}
        {!group && (
          <Button variant="outline" onClick={() => setGroupModalOpen(true)}>
            <UsersFourIcon className="mr-2 size-4" />
            Add to Group
          </Button>
        )}

        <FeatureSwitch
          feature="assessments"
          enabled={
            <Tooltip>
              <TooltipTrigger asChild>
                {/* wrap menu item in span so tooltip doesn't get disabled */}
                <span>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => {
                      setMoreMenuOpen(false);
                      trackMixpanelEvent(
                        'Create Assessment clicked from Selected Learner Options',
                      );
                      setAssessmentDialogueOpen(true);
                    }}
                    disabled={!!createAssessmentsDisabledTooltip}
                  >
                    <PencilLineIcon className="size-4" />
                    <span>Create Assessment</span>
                    {createAssessmentsDisabledTooltip && (
                      <Info color="hsl(var(--primary))" size={16} />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {createAssessmentsDisabledTooltip && (
                <TooltipContent>
                  {createAssessmentsDisabledTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          }
          disabled={
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                setMoreMenuOpen(false);
                dispatch(setAttemptedProFeature('Assessments'));
              }}
            >
              <PencilLineIcon className="mr-2 size-4" />
              Create Assessment
              <ProIcon />
            </Button>
          }
        />

        <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
          <GearIcon className="mr-2 size-4" />
          Settings
        </Button>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <FileArrowUpIcon className="mr-2 size-4" />
              Export
              <CaretUpIcon className="ml-2 size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" sideOffset={8}>
            <DropdownMenuItem asChild>
              <CSVLink
                data={csvData}
                headers={csvFields}
                filename={csvFileName}
                target="_blank"
              >
                <TableIcon className="mr-2 size-4" />
                Export as CSV
              </CSVLink>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => generateLoginCards(selectedStudents)}
            >
              <CardsIcon className="mr-2 size-4" />
              Export as Cards
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* More overflow menu (feature-gated items) */}
        <DropdownMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              More
              <CaretUpIcon className="ml-2 size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" sideOffset={8}>
            <DropdownMenuItem onClick={() => setLevelChangeDialogOpen(true)}>
              <ArrowsDownUpIcon className="mr-2 size-4" />
              Change Level
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => setSignInChangeDialogOpen(true)}>
              <KeyIcon className="mr-2 size-4" />
              Change Sign-Ins
            </DropdownMenuItem>

            <FeatureSwitch
              feature="insights"
              enabled={
                <DropdownMenuItem
                  onClick={() => {
                    setMoreMenuOpen(false);
                    handleViewInsights();
                  }}
                >
                  <ChartLineIcon className="mr-2 size-4" />
                  View Insights
                </DropdownMenuItem>
              }
              disabled={
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => {
                    setMoreMenuOpen(false);
                    dispatch(setAttemptedProFeature('Insights'));
                  }}
                >
                  <ChartLineIcon className="mr-2 size-4" />
                  View Insights
                  <ProIcon />
                </DropdownMenuItem>
              }
            />

            {/* Delete / Remove from group */}
            {group ? (
              <DropdownMenuItem onClick={handleConfirmRemoveFromGroup}>
                <Trash2
                  className="mr-2 size-4"
                  color="hsl(var(--destructive))"
                />
                Remove from group
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleConfirmDeleteSelectedStudents}>
                <Trash2
                  className="mr-2 size-4"
                  color="hsl(var(--destructive))"
                />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Close */}
        <Button variant="ghost" className="px-2" onClick={onClose}>
          <XIcon size={16} />
        </Button>
      </StudentOptionsButtonGroup>

      <AddToGroupModal
        open={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        students={selectedStudents}
      />

      <BulkLevelChangeDialog
        open={levelChangeDialogOpen}
        onClose={() => setLevelChangeDialogOpen(false)}
        students={selectedStudents}
      />

      <BulkUpdateSignIns
        open={signInChangeDialogOpen}
        onClose={() => setSignInChangeDialogOpen(false)}
        students={selectedStudents}
      />

      <BulkSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        students={selectedStudents}
      />

      <CreateAssessmentDialogue
        isOpen={assessmentDialogueOpen}
        onClose={() => setAssessmentDialogueOpen(false)}
        initialStudents={Array.from(selectedStudentIds)}
      />
    </StudentOptionsContainer>
  );
}

const StudentOptionsContainer = styled.div`
  position: fixed;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 90vw;
  width: fit-content;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fff;
  box-shadow:
    0 4px 12px 0 rgba(0, 0, 0, 0.1),
    0 1px 3px 0 rgba(0, 0, 0, 0.08);
  z-index: 49;

  & > p {
    font-size: 14px;
  }
`;

const StudentOptionsButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
